// MacroThemeMap — interactive map of curated signals grouped by market zone.
// (Governed v5: `governance.marketZone`. Legacy fallback: `MacroTheme`.)
// Clicking a zone calls `onSelectMacroTheme` so the parent can filter the
// signal board. Visual language matches the rest of the Market View.

import React, { useMemo } from "react";
import { marketZoneForTrack } from "../../lib/tracks";

const MacroThemeMap = ({ tracks, selectedMacroTheme, onSelectMacroTheme, onClearMacroTheme, compact = false }) => {
    const counts = useMemo(() => {
        const map = new Map();
        for (const t of tracks || []) {
            // Only top-level tracks contribute to the zone map. Sub-patterns
            // and weekly dynamics inherit context from their parent and
            // shouldn't double-count here.
            if (t._governed && !t._governed.isTopLevel) continue;
            const k = marketZoneForTrack(t) || "(no market zone)";
            const cur = map.get(k) || { core: 0, watchlist: 0 };
            if (t._role === "CORE_TRACK") cur.core += 1;
            else if (t._role === "WATCHLIST_TRACK") cur.watchlist += 1;
            map.set(k, cur);
        }
        return [...map.entries()]
            .map(([k, v]) => ({ theme: k, ...v, total: v.core + v.watchlist }))
            .filter((r) => r.total > 0)
            .sort((a, b) => b.total - a.total);
    }, [tracks]);

    if (!counts.length) return null;
    const max = Math.max(1, ...counts.map((r) => r.total));
    const padding = compact ? "p-4" : "p-5";

    return (
        <div className={`card-flat ${padding}`} data-testid="macro-theme-map">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                    <div className="eyebrow">Market structure map</div>
                    <h3 className={`display ${compact ? "text-base" : "text-lg"} mt-1`}>Signals by market zone</h3>
                </div>
                {selectedMacroTheme && (
                    <button
                        type="button"
                        data-testid="clear-macro-theme"
                        onClick={() => onClearMacroTheme && onClearMacroTheme()}
                        className="text-[11px] font-medium text-[hsl(var(--accent-ink))] hover:underline"
                    >
                        Clear filter ×
                    </button>
                )}
            </div>
            <p className="text-[11px] text-[hsl(var(--mute))] mt-1.5">Click a zone or segment to filter the signal table.</p>
            <ul className={`${compact ? "mt-3 space-y-1.5" : "mt-4 space-y-2.5"}`}>
                {counts.map((r) => {
                    const active = selectedMacroTheme === r.theme;
                    const clickable = r.theme !== "(no market zone)";
                    return (
                        <li key={r.theme}>
                            <button
                                type="button"
                                data-testid={`macro-theme-${r.theme}`}
                                onClick={() => clickable && onSelectMacroTheme && onSelectMacroTheme(active ? null : r.theme)}
                                disabled={!clickable}
                                aria-pressed={active}
                                className={`w-full flex items-center gap-3 px-2 py-1 -mx-2 rounded-sm text-left transition-colors ${active ? "bg-[hsl(var(--accent-soft))]" : clickable ? "hover:bg-[hsl(var(--surface-sunken))]" : ""}`}
                            >
                                <span className={`text-xs ${compact ? "w-44" : "w-56"} truncate flex-none ${active ? "text-[hsl(var(--accent-ink))] font-medium" : "text-[hsl(var(--ink-soft))]"}`} title={r.theme}>{r.theme}</span>
                                <div className="flex-1 flex h-2 rounded-sm overflow-hidden bg-[hsl(var(--surface-sunken))]">
                                    {r.core > 0 && <div className="h-full bg-[hsl(var(--ink))]" style={{ width: `${(r.core / max) * 100}%` }} title={`${r.core} core`} />}
                                    {r.watchlist > 0 && <div className="h-full bg-[hsl(var(--warning))]" style={{ width: `${(r.watchlist / max) * 100}%` }} title={`${r.watchlist} watchlist`} />}
                                </div>
                                <span className="text-[11px] num text-[hsl(var(--mute))] w-14 text-right">{r.core}c · {r.watchlist}w</span>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default MacroThemeMap;
